/*
 * SolidForms: Compare 3D printers (sections/sf-compare.liquid).
 *
 * Every product in the section's collection becomes a choice. Its specs are
 * merged from three layers, later winning:
 *   1. parsed from the product description (so a newly added printer shows up
 *      with whatever its description states, with no other work),
 *   2. the curated seed (snippets/sf-compare-seed.liquid), keyed by handle,
 *   3. the product's `specs.*` metafields, for exact manual control.
 * A spec no layer states shows as "Not listed"; nothing is guessed.
 *
 * The selection lives in the URL (?compare=handle,handle) so a comparison can
 * be shared. Two printers, A and B, side by side in a bordered table.
 */
(function () {
  'use strict';

  var reduce = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
  var hover = window.matchMedia && matchMedia('(hover: hover) and (pointer: fine)').matches;
  var SLOT_COLORS = ['#ff6b35', '#2dd4bf'];
  var SLOT_NAMES = ['A', 'B'];

  function $(sel, el) { return (el || document).querySelector(sel); }
  function $$(sel, el) { return Array.prototype.slice.call((el || document).querySelectorAll(sel)); }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  // `t` output arrives HTML-escaped; decode once so it can be re-escaped safely.
  var deco = document.createElement('textarea');
  function unesc(s) { deco.innerHTML = s; return deco.value; }
  function fmtN(n) { return Number(n).toLocaleString('en-US', { maximumFractionDigits: 1 }); }
  function readJSON(el) { try { return JSON.parse(el.textContent); } catch (e) { return null; } }

  /* ------------------------------------------------------------ spec parsing */

  function toVol(s) {
    if (!s) return null;
    if (Array.isArray(s)) return s.length === 3 ? s.map(Number) : null;
    var m = String(s).match(/(\d{2,4})\s*(?:mm)?\s*[x×*]\s*(\d{2,4})\s*(?:mm)?\s*[x×*]\s*(\d{2,4})/i);
    return m ? [+m[1], +m[2], +m[3]] : null;
  }
  function maxOf(re, text, lo, hi) {
    var best = null, m;
    re.lastIndex = 0;
    while ((m = re.exec(text))) {
      var v = parseFloat(String(m[1]).replace(/,/g, ''));
      if (v >= lo && v <= hi && (best === null || v > best)) best = v;
    }
    return best;
  }

  // Reads what a product description states. Deliberately conservative: a
  // number only counts when the words around it say what it is.
  function parseDesc(d, tags) {
    var t = (d || '').replace(/\s+/g, ' ');
    var s = {};
    var bv = toVol(t);
    if (!bv) {
      var cube = t.match(/(\d{3})\s*mm\s*(?:cubed|cube|³)/i);
      if (cube) bv = [+cube[1], +cube[1], +cube[1]];
    }
    if (bv) s.bv = bv;
    var acc = maxOf(/([\d,]{4,6})\s*mm\/s(?:²|\^2|2)/gi, t, 1000, 100000);
    if (acc) s.accel = acc;
    var spd = maxOf(/(\d{3,4})\s*mm\/s(?![²2^])/gi, t, 100, 2000);
    if (spd) s.speed = spd;
    var noz = maxOf(/(\d{3})\s*°?\s*[C℃]\s*(?:high[- ]temp(?:erature)?\s*)?(?:hotend|nozzle|printing)/gi, t, 200, 500)
      || maxOf(/(?:hotend|nozzle)(?:\s*temp(?:erature)?)?\s*:?\s*(?:up to\s*)?(\d{3})\s*°?\s*[C℃]/gi, t, 200, 500);
    if (noz) s.nozzle = noz;
    var bed = maxOf(/(\d{2,3})\s*°?\s*[C℃]\s*heated bed/gi, t, 40, 200)
      || maxOf(/bed(?:\s*temp(?:erature)?)?\s*:?\s*(?:up to\s*)?(\d{2,3})\s*°?\s*[C℃]/gi, t, 40, 200);
    if (bed) s.bed = bed;
    var ch = t.match(/(\d{2})\s*°?\s*[C℃]\s*(?:active(?:ly)?\s*)?(?:heated\s*)?chamber/i);
    if (ch || /active(?:ly)?\s*heated\s*chamber|chamber heating/i.test(t)) {
      s.enc = 'heated';
      if (ch) s.chamber = +ch[1];
    } else if (/optional enclosure/i.test(t)) s.enc = 'optional';
    else if (/(?:fully[- ])?enclosed|enclosure/i.test(t)) s.enc = 'enclosed';
    else if (/open[- ]frame/i.test(t)) s.enc = 'open';
    var tg = (tags || []).join(' ').toLowerCase();
    if (/core\s?xy/i.test(t) || /corexy/.test(tg)) s.motion = 'corexy';
    else if (/core\s?xz/i.test(t)) s.motion = 'corexz';
    var cmax = maxOf(/(\d{1,2})[- ]colou?r/gi, t, 2, 64);
    if (cmax) s.colors = [null, cmax];
    else if (/multicolor/.test(tg)) s.colors = [null, null];
    var db = maxOf(/(\d{2})\s*dB/gi, t, 25, 90);
    if (db) s.noise = db;
    if (/camera/i.test(t)) s.cam = true;
    return s;
  }

  // The "Full Specifications" list every printer description ends with, one
  // "Label: value" per line, in English or Arabic (the storefront serves the
  // visitor's language). This is the source of truth: edit the description in
  // Shopify and the comparison follows. "Not specified" lines are skipped.
  var SPEC_LABELS = {
    'build volume': 'bv', 'حجم الطباعة': 'bv',
    'max print speed': 'speed', 'أقصى سرعة طباعة': 'speed',
    'max acceleration': 'accel', 'أقصى تسارع': 'accel',
    'max nozzle temperature': 'nozzle', 'أقصى حرارة للفوهة': 'nozzle',
    'max bed temperature': 'bed', 'أقصى حرارة للسطح': 'bed',
    'enclosure': 'enc', 'الغلاف': 'enc',
    'motion system': 'motion', 'نظام الحركة': 'motion',
    'multicolor': 'colors', 'تعدد الألوان': 'colors',
    'noise level': 'noise', 'مستوى الضوضاء': 'noise',
    'camera': 'cam', 'الكاميرا': 'cam',
    'weight': 'weight', 'الوزن': 'weight',
    'max power draw': 'power', 'أقصى استهلاك للكهرباء': 'power'
  };
  function parseSpecList(d) {
    var s = {}, found = false;
    (d || '').split(/\n/).forEach(function (line) {
      var m = line.trim().match(/^([^:]{3,40}):\s*(.+)$/);
      if (!m) return;
      var key = SPEC_LABELS[m[1].trim().toLowerCase()], v = m[2].trim();
      if (!key || /not specified|غير محدد/i.test(v)) return;
      found = true;
      var n = parseFloat(v.replace(/,/g, '').replace(/^[^\d]*/, ''));
      switch (key) {
        case 'bv': if (toVol(v)) s.bv = toVol(v); break;
        case 'enc':
          if (/heated|مُسخّنة|مسخنة/i.test(v)) { s.enc = 'heated'; if (n) s.chamber = n; }
          else if (/optional|اختياري/i.test(v)) s.enc = 'optional';
          else if (/open|مفتوحة/i.test(v)) s.enc = 'open';
          else if (/enclosed|مغلقة/i.test(v)) s.enc = 'enclosed';
          break;
        case 'motion':
          s.motion = /corexy/i.test(v) ? 'corexy' : /corexz/i.test(v) ? 'corexz' : /cartesian/i.test(v) ? 'cartesian' : /slinger|سرير/i.test(v) ? 'bedslinger' : undefined;
          break;
        case 'colors': {
          if (/^(single color|لون واحد)$/i.test(v)) { s.colors = [1, 1]; break; }
          var inc = v.match(/(\d+)\s*(?:colou?rs?\s+included|ألوان\s+معاها|لون\s+معاها)/i);
          var one = /^(1 colou?r included|لون واحد معاها)/i.test(v);
          var max = v.match(/(?:up to|لحد)\s*(\d+)/i);
          var sys = v.match(/\(([^)]+)\)\s*$/);
          // "4 colors included" with no upgrade path means 4 is also the maximum;
          // "multicolor-ready" leaves the maximum open
          var ready = /ready|جاهزة/i.test(v);
          var i0 = inc ? +inc[1] : one ? 1 : null;
          s.colors = [i0, max ? +max[1] : (i0 && !ready ? i0 : null)];
          if (sys) s.system = sys[1];
          break;
        }
        case 'cam': s.cam = /built-in|مدمجة/i.test(v); break;
        default: if (!isNaN(n)) s[key] = n;
      }
    });
    return found ? s : {};
  }

  function fromMetafields(m) {
    if (!m) return {};
    var s = {};
    function n(k) { return m[k] == null || m[k] === '' ? null : Number(m[k]); }
    if (toVol(m.build_volume)) s.bv = toVol(m.build_volume);
    if (n('max_speed')) s.speed = n('max_speed');
    if (n('acceleration')) s.accel = n('acceleration');
    if (n('nozzle_temp')) s.nozzle = n('nozzle_temp');
    if (n('bed_temp')) s.bed = n('bed_temp');
    if (m.enclosure) s.enc = String(m.enclosure).toLowerCase();
    if (n('chamber_temp')) s.chamber = n('chamber_temp');
    if (m.motion) s.motion = String(m.motion).toLowerCase().replace(/[^a-z]/g, '');
    if (n('colors_included') || n('colors_max')) s.colors = [n('colors_included'), n('colors_max')];
    if (m.color_system) s.system = m.color_system;
    if (n('noise_db')) s.noise = n('noise_db');
    if (m.camera != null && m.camera !== '') s.cam = m.camera === true || m.camera === 'true';
    if (n('weight_kg')) s.weight = n('weight_kg');
    if (n('power_w')) s.power = n('power_w');
    return s;
  }

  // The "SolidForms Recommendation" block in the description (English or
  // Arabic): a verdict line, then the pros list, then the cons list.
  var RECO_HEADS = {
    start: /^(solidforms recommendation|توصية سوليد فورمز)$/i,
    pros: /^(what we like|اللي عاجبنا)$/i,
    cons: /^(what to know|اللي لازم تعرفه)$/i,
    end: /^(buy it if|skip it if|اشتريها لو|بلاش منها لو|full specifications|المواصفات الكاملة)/i
  };
  function parseReco(d) {
    var lines = (d || '').split(/\n/).map(function (l) { return l.trim(); }).filter(Boolean);
    var i = -1;
    for (var k = 0; k < lines.length; k++) if (RECO_HEADS.start.test(lines[k])) { i = k; break; }
    if (i < 0) return null;
    var r = { verdict: '', pros: [], cons: [] }, mode = 'verdict';
    for (var j = i + 1; j < lines.length; j++) {
      var l = lines[j];
      if (RECO_HEADS.end.test(l)) break;
      if (RECO_HEADS.pros.test(l)) { mode = 'pros'; continue; }
      if (RECO_HEADS.cons.test(l)) { mode = 'cons'; continue; }
      if (mode === 'verdict') r.verdict = r.verdict ? r.verdict + ' ' + l : l;
      else r[mode].push(l);
    }
    return r.verdict || r.pros.length || r.cons.length ? r : null;
  }

  // Arabic titles start with "طابعة" (printer); the brand is the first Latin word.
  function bareTitle(title) { return title.replace(/^[؀-ۿ\s]+/, ''); }
  function brandOf(title) {
    var w = bareTitle(title).split(/\s+/);
    if (/^bambu$/i.test(w[0])) return 'Bambu Lab';
    return w[0];
  }

  /* --------------------------------------------------------- illustrations */
  // Small, self-explaining drawings for the spec explainers. Stroke colours
  // come from CSS (currentColor + --a accent) so they work in both directions.
  var ILL = {
    bv: '<svg viewBox="0 0 240 150"><g class="ln"><path d="M70 45 120 20 170 45 170 105 120 130 70 105Z"/><path d="M70 45 120 70 170 45M120 70v60"/></g><g class="ac grow"><path d="M108 112c-4-14 2-26 0-38 6-6 18-6 24 0-2 12 4 24 0 38-6 4-18 4-24 0Z"/></g><g class="dim"><path d="M62 112 112 137"/><text x="74" y="140">X</text><path d="M178 112 128 137"/><text x="158" y="140">Y</text><path d="M180 40v60"/><text x="186" y="74">Z</text></g></svg>',
    speed: '<svg viewBox="0 0 240 150"><g class="ln"><path d="M20 40h200"/><rect x="20" y="118" width="200" height="8" rx="3"/></g><g class="ac move"><rect x="40" y="28" width="44" height="34" rx="5"/><path d="M58 62h8l-4 10z"/><path class="trail" d="M26 34h8M20 46h12M26 56h8"/></g><g class="ac2"><path class="draw" d="M30 112h180"/></g></svg>',
    accel: '<svg viewBox="0 0 240 150"><g class="ln"><path d="M30 20v110h190"/></g><g class="ac"><path class="draw" d="M30 130c10-60 20-90 50-100h130"/></g><g class="mut"><path class="draw d2" d="M30 130c40-30 80-70 120-100h60"/></g><text class="lab" x="86" y="24">fast</text><text class="lab mt" x="150" y="62">slow</text></svg>',
    nozzle: '<svg viewBox="0 0 240 150"><g class="ln"><rect x="60" y="20" width="60" height="44" rx="6"/><path d="M80 64v20h20V64M84 84l6 12 6-12"/></g><g class="heat"><path d="M74 108c-6-8 6-12 0-20M90 112c-6-8 6-12 0-20M106 108c-6-8 6-12 0-20"/></g><g class="scale"><path d="M160 130V22"/><path d="M156 112h8M156 94h8M156 78h8M156 56h8M156 36h8"/><text x="170" y="116">PLA</text><text x="170" y="98">PETG</text><text x="170" y="82">ABS</text><text x="170" y="60">PC</text><text x="170" y="40">PA-CF</text></g></svg>',
    bed: '<svg viewBox="0 0 240 150"><g class="ln"><path d="M30 112 120 92 210 112 120 132Z"/></g><g class="ac"><path d="M96 104h48v-26l-6-10h-36l-6 10Z"/></g><g class="heat"><path d="M60 90c-6-8 6-12 0-20M180 90c-6-8 6-12 0-20M120 66c-6-8 6-12 0-20"/></g></svg>',
    enc: '<svg viewBox="0 0 240 150"><g class="ln"><rect x="56" y="18" width="128" height="114" rx="8"/><path d="M120 18v114"/></g><g class="ac"><rect x="100" y="96" width="40" height="22" rx="3"/></g><g class="warm"><path class="spin" d="M84 70a36 26 0 1 0 72 0a36 26 0 1 0-72 0" fill="none" stroke-dasharray="10 8"/></g><g class="cold"><path d="M8 50h34M14 70h30M8 90h34"/><path d="M42 50l-6-4M42 50l-6 4"/></g></svg>',
    motion: '<svg viewBox="0 0 240 150"><g class="ln"><rect x="12" y="104" width="96" height="8" rx="2" class="slide"/><path d="M20 30h80M60 30v40"/><rect x="50" y="70" width="20" height="12" rx="2"/></g><g class="ac slide"><path d="M50 104v-26h20v26"/></g><g class="ln"><rect x="132" y="22" width="96" height="96" rx="4"/><path d="M132 52h96M180 22v96"/><rect x="138" y="96" width="84" height="8" rx="2" class="drop"/></g><g class="ac"><rect class="xy" x="172" y="44" width="16" height="16" rx="3"/></g><text class="lab" x="22" y="136">bed slinger</text><text class="lab" x="160" y="136">CoreXY</text></svg>',
    colors: '<svg viewBox="0 0 240 150"><g class="spools"><circle cx="40" cy="36" r="16" fill="#ff6b35"/><circle cx="90" cy="36" r="16" fill="#2dd4bf"/><circle cx="140" cy="36" r="16" fill="#a78bfa"/><circle cx="190" cy="36" r="16" fill="#facc15"/></g><g class="ln"><path d="M40 52c0 30 70 40 80 60M90 52c0 20 30 30 30 60M140 52c0 20-20 30-20 60M190 52c0 30-70 40-70 60"/><path d="M110 112h20v12l-10 10-10-10z"/></g></svg>',
    materials: '<svg viewBox="0 0 240 150"><g class="spools"><circle cx="40" cy="64" r="22" fill="#e5e7eb"/><circle cx="96" cy="64" r="22" fill="#60a5fa"/><circle cx="152" cy="64" r="22" fill="#f87171"/><circle cx="208" cy="64" r="22" fill="#374151"/></g><g class="lab"><text x="28" y="112">PLA</text><text x="80" y="112">PETG</text><text x="138" y="112">ABS</text><text x="190" y="112">PA-CF</text></g><g class="ln"><path d="M18 128h206"/></g><g class="ac"><path d="M18 128h60"/></g><g class="ac2"><path d="M78 128h56"/></g><g class="hot"><path d="M134 128h90"/></g></svg>',
    noise: '<svg viewBox="0 0 240 150"><g class="ln"><path d="M30 60h20l26-22v74L50 90H30Z"/></g><g class="ac waves"><path d="M94 56c10 10 10 28 0 38"/><path d="M110 46c18 16 18 42 0 58"/><path d="M126 36c26 22 26 56 0 78"/></g><g class="scale"><path d="M170 130V22"/><path d="M166 110h8M166 80h8M166 50h8"/><text x="180" y="114">40 library</text><text x="180" y="84">50 office</text><text x="180" y="54">60 talk</text></g></svg>',
    cam: '<svg viewBox="0 0 240 150"><g class="ln"><rect x="20" y="30" width="64" height="44" rx="8"/><circle cx="52" cy="52" r="12"/><path d="M84 52 196 104"/></g><g class="ac"><path class="draw" d="M150 112c10-14 20 8 30-6s14 12 26 0"/></g><g class="bad"><path d="M196 48l20 20M216 48l-20 20"/></g></svg>'
  };

  // Simple glyphs used inside the table cells.
  var ICON = {
    open: '<svg viewBox="0 0 32 32"><path d="M5 27V6h22v21M5 9h22"/></svg>',
    enclosed: '<svg viewBox="0 0 32 32"><rect x="5" y="5" width="22" height="22" rx="3"/><path d="M16 5v22"/></svg>',
    heated: '<svg viewBox="0 0 32 32"><rect x="5" y="5" width="22" height="22" rx="3"/><path d="M12 22c-3-4 3-6 0-10M17 22c-3-4 3-6 0-10M22 22c-3-4 3-6 0-10"/></svg>',
    optional: '<svg viewBox="0 0 32 32"><path d="M5 27V6h22v21M5 9h22"/><path d="M5 6h22v21" stroke-dasharray="3 3"/></svg>',
    yes: '<svg viewBox="0 0 24 24"><path d="m5 12.5 4.5 4.5L19 7.5"/></svg>',
    no: '<svg viewBox="0 0 24 24"><path d="M7 7l10 10M17 7 7 17"/></svg>',
    part: '<svg viewBox="0 0 24 24"><path d="M6 12h12"/></svg>',
    plus: '<svg viewBox="0 0 24 24"><path d="M12 5v14M5 12h14"/></svg>',
    swap: '<svg viewBox="0 0 24 24"><path d="M7 7h11l-3-3M17 17H6l3 3"/></svg>',
    x: '<svg viewBox="0 0 24 24"><path d="M7 7l10 10M17 7 7 17"/></svg>',
    search: '<svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="6.5"/><path d="m16 16 4.5 4.5"/></svg>',
    link: '<svg viewBox="0 0 24 24"><path d="M10 14a4 4 0 0 0 5.66 0l3-3a4 4 0 0 0-5.66-5.66l-1 1M14 10a4 4 0 0 0-5.66 0l-3 3a4 4 0 0 0 5.66 5.66l1-1"/></svg>'
  };

  /* --------------------------------------------------------------- the app */

  function Compare(root) {
    if (root.sfcm) return;
    root.sfcm = true;
    var app = $('[data-sfcm-app]', root);
    var raw = readJSON($('[data-sfcm-products]', root)) || [];
    var seed = readJSON($('[data-sfcm-seed]', root)) || {};
    var L = readJSON($('[data-sfcm-i18n]', root)) || {};
    Object.keys(L).forEach(function (k) { L[k] = unesc(L[k]); });
    function T(k, vars) {
      var s = L[k] || k;
      if (vars) Object.keys(vars).forEach(function (v) { s = s.split('{' + v + '}').join(vars[v]); });
      return s;
    }

    var printers = raw.map(function (p) {
      var s = Object.assign({}, parseDesc(p.d, p.tags), seed[p.h] || {}, parseSpecList(p.d), fromMetafields(p.mf));
      if (s.bv) s.litres = s.bv[0] * s.bv[1] * s.bv[2] / 1e6;
      return {
        h: p.h, title: unesc(p.t), brand: brandOf(unesc(p.t)), url: p.u, img: p.img,
        price: p.p, compare: p.c && p.c > p.p ? p.c : null, ps: unesc(p.ps || ''), cs: p.c && p.c > p.p ? unesc(p.cs || '') : '',
        available: p.a, vid: p.vid, nv: p.nv, tags: p.tags || [], s: s, reco: parseReco(p.d)
      };
    });
    var byH = {};
    printers.forEach(function (p) { byH[p.h] = p; });
    if (!printers.length) return;
    var pairsCfg = readJSON($('[data-sfcm-matchups]', root)) || '';

    function cap() { return 2; }

    /* -------- rows: what each spec is, how it reads, which way is better */
    function catMax(f) { return printers.reduce(function (m, p) { var v = f(p.s); return v > m ? v : m; }, 0) || 1; }
    var ENC_RANK = { open: 1, optional: 2, enclosed: 3, heated: 4 };
    var MATS = ['pla', 'petg', 'tpu', 'abs', 'pc', 'cf', 'eng'];
    function mats(s) {
      var enc = s.enc, noz = s.nozzle, out = {};
      var closed = enc === 'enclosed' || enc === 'heated';
      out.pla = 2; out.petg = 2; out.tpu = 2;
      out.abs = enc ? (closed ? 2 : 1) : null;
      out.pc = noz == null || !enc ? null : noz < 300 ? 0 : closed ? 2 : 1;
      out.cf = out.pc;
      out.eng = noz == null || !enc ? null : noz >= 320 && enc === 'heated' ? 2 : noz >= 320 && closed ? 1 : 0;
      return out;
    }
    function matScore(s) { var m = mats(s), n = 0; MATS.forEach(function (k) { n += m[k] || 0; }); return m.pc == null ? null : n; }

    var GROUPS = [
      { id: 'reco', rows: ['verdict', 'pros', 'cons'] },
      { id: 'size', rows: ['bv', 'size'] },
      { id: 'speed', rows: ['speed', 'accel'] },
      { id: 'heat', rows: ['nozzle', 'bed', 'enc', 'materials'] },
      { id: 'build', rows: ['colors', 'motion', 'noise', 'cam', 'weight', 'power'] },
      { id: 'price', rows: ['price'] }
    ];
    function list(items, cls) {
      return items && items.length ? '<ul class="sfcm-pc ' + cls + '">' + items.map(function (t) { return '<li>' + esc(t) + '</li>'; }).join('') + '</ul>' : null;
    }
    var ROWS = {
      verdict: { val: function (s, p) { return p.reco && p.reco.verdict || null; },
        html: function (s, p) { return p.reco && p.reco.verdict ? '<p class="sfcm-verdict">' + esc(p.reco.verdict) + '</p>' : null; } },
      pros: { val: function (s, p) { return p.reco && p.reco.pros.length || null; },
        html: function (s, p) { return list(p.reco && p.reco.pros, 'is-pro'); } },
      cons: { val: function (s, p) { return p.reco && p.reco.cons.length || null; },
        html: function (s, p) { return list(p.reco && p.reco.cons, 'is-con'); } },
      size: { joint: true, val: function () { return null; } },
      bv: { info: 'bv', better: 'high', val: function (s) { return s.litres || null; },
        txt: function (s) { return s.bv ? s.bv.join(' × ') + ' mm' : null; },
        sub: function (s) { return s.litres ? fmtN(s.litres) + ' ' + T('vol_unit') : ''; } },
      speed: { info: 'speed', better: 'high', unit: 'mm/s', bar: true, val: function (s) { return s.speed || null; } },
      accel: { info: 'accel', better: 'high', unit: 'mm/s²', bar: true, val: function (s) { return s.accel || null; } },
      nozzle: { info: 'nozzle', better: 'high', unit: '°C', thermo: [[210, 'PLA'], [260, 'ABS'], [290, 'PC'], [320, 'PA-CF']], lo: 180, hi: 360, val: function (s) { return s.nozzle || null; } },
      bed: { info: 'bed', better: 'high', unit: '°C', thermo: [[60, 'PLA'], [80, 'PETG'], [100, 'ABS'], [110, 'PC']], lo: 40, hi: 125, val: function (s) { return s.bed || null; } },
      enc: { info: 'enc', better: 'high', val: function (s) { return ENC_RANK[s.enc] || null; },
        html: function (s) {
          if (!s.enc) return null;
          var sub = s.enc === 'heated' && s.chamber ? '<small>' + esc(T('heated_to', { n: s.chamber })) + '</small>' : '';
          return '<span class="sfcm-ico">' + ICON[s.enc] + '</span><span>' + esc(T('enc_' + s.enc)) + sub + '</span>';
        } },
      materials: { info: 'materials', better: 'high', val: function (s) { return matScore(s); },
        html: function (s) {
          if (matScore(s) == null) return null;
          var m = mats(s);
          return '<ul class="sfcm-mats">' + MATS.map(function (k) {
            var st = m[k] === 2 ? 'ok' : m[k] === 1 ? 'part' : 'no';
            var ic = st === 'ok' ? ICON.yes : st === 'part' ? ICON.part : ICON.no;
            return '<li class="is-' + st + '" title="' + esc(T('mat_' + st)) + '">' + ic + '<span>' + esc(T('m_' + k)) + '</span></li>';
          }).join('') + '</ul>';
        } },
      colors: { info: 'colors', better: 'high', val: function (s) { return s.colors ? (s.colors[1] || s.colors[0] || null) : null; },
        html: function (s) {
          if (!s.colors) return null;
          var inc = s.colors[0], max = s.colors[1];
          // one spool and no stated upgrade path reads as single color
          if (max === 1 || (inc === 1 && max == null)) return '<span>' + esc(T('colors_single')) + '</span>';
          var shown = Math.min(max || inc || 0, 16), dots = '';
          var pal = ['#ff6b35', '#2dd4bf', '#a78bfa', '#facc15', '#f472b6', '#60a5fa', '#4ade80', '#f87171'];
          for (var i = 0; i < shown; i++) dots += '<i' + (inc && i < inc ? ' style="--c:' + pal[i % pal.length] + '"' : ' class="is-ghost"') + '></i>';
          var more = (max || 0) > 16 ? '<b>+' + (max - 16) + '</b>' : '';
          var label = inc && max && inc === max ? T('colors_inc', { i: inc }) : inc && max ? T('colors_val', { i: inc, m: max }) : max ? T('colors_upto', { m: max }) : inc ? T('colors_inc', { i: inc }) : T('colors_multi');
          var sys = s.system ? ' <small>' + esc(s.system) + '</small>' : '';
          return (shown ? '<span class="sfcm-dots">' + dots + more + '</span>' : '') + '<span>' + esc(label) + sys + '</span>';
        } },
      motion: { info: 'motion', val: function (s) { return s.motion || null; },
        txt: function (s) { return s.motion ? T('motion_' + s.motion) : null; } },
      noise: { info: 'noise', better: 'low', unit: 'dB', val: function (s) { return s.noise || null; },
        sub: function (s) { return !s.noise ? '' : T(s.noise <= 45 ? 'noise_quiet' : s.noise <= 52 ? 'noise_office' : s.noise <= 60 ? 'noise_talk' : 'noise_loud'); } },
      cam: { info: 'cam', better: 'high', val: function (s) { return s.cam == null ? null : s.cam ? 1 : 0; },
        html: function (s) { return s.cam == null ? null : '<span class="sfcm-yn is-' + (s.cam ? 'y' : 'n') + '">' + (s.cam ? ICON.yes : ICON.no) + esc(T(s.cam ? 'yes' : 'no')) + '</span>'; } },
      weight: { unit: 'kg', val: function (s) { return s.weight || null; } },
      power: { unit: 'W', val: function (s) { return s.power || null; } },
      price: { better: 'low', val: function (s, p) { return p.price; },
        html: function (s, p) {
          return '<span class="sfcm-pp">' + (p.cs ? '<s>' + esc(p.cs) + '</s> ' : '') + '<b>' + esc(p.ps) + '</b></span>' +
            (p.available ? '' : '<small class="sfcm-out">' + esc(T('sold_out')) + '</small>');
        } }
    };
    Object.keys(ROWS).forEach(function (k) { if (ROWS[k].bar) ROWS[k].max = catMax(ROWS[k].val); });

    /* -------- state */
    var sel = [null, null];   // slot A, slot B; either may be empty
    function readURL() {
      var q = new URLSearchParams(location.search).get('compare');
      var hs = q ? q.split(',').slice(0, 2) : [];
      return [byH[hs[0]] ? hs[0] : null, byH[hs[1]] ? hs[1] : null];
    }
    function writeURL() {
      try {
        var u = new URL(location.href);
        if (sel[0] || sel[1]) u.searchParams.set('compare', (sel[0] || '') + ',' + (sel[1] || ''));
        else u.searchParams.delete('compare');
        history.replaceState(history.state, '', u.toString());
      } catch (e) {}
    }

    // Matchups come from the section setting, one per line: "P2S vs X2D".
    // Each side is a product handle or words from the title; the shortest
    // matching title wins (so "A1" picks the A1, not the A1 Combo). A pair
    // whose printer is not in the collection yet is skipped until it is added.
    function find(q) {
      q = q.trim().toLowerCase();
      if (byH[q]) return byH[q];
      var words = q.split(/\s+/).filter(Boolean), hit = null;
      printers.forEach(function (p) {
        var t = bareTitle(p.title).toLowerCase(), tw = t.split(/\s+/);
        if (!words.every(function (w) { return tw.indexOf(w) > -1; })) return;
        if (!hit || t.length < bareTitle(hit.title).length) hit = p;
      });
      return hit;
    }
    var pairs = String(pairsCfg).split(/\n/).map(function (line) {
      var sides = line.split(/\s+vs\.?\s+/i);
      if (sides.length !== 2) return null;
      var a = find(sides[0]), b = find(sides[1]);
      return a && b && a !== b ? [a.h, b.h] : null;
    }).filter(Boolean);
    function matchups() { return pairs; }

    /* -------- render */
    function shell() {
      app.innerHTML =
        '<div class="sfcm-quick" data-quick></div>' +
        '<div class="sfcm-slots" data-slots></div>' +
        '<div class="sfcm-tools" data-tools hidden><button type="button" class="sfcm-share" data-share>' + ICON.link + '<span>' + esc(T('share')) + '</span></button></div>' +
        '<div class="sfcm-sticky" data-sticky aria-hidden="true"></div>' +
        '<div class="sfcm-table" data-table></div>' +
        '<div class="sfcm-pop" data-pop role="dialog" aria-modal="false" hidden></div>' +
        '<div class="sfcm-sheet" data-picker role="dialog" aria-modal="true" aria-label="' + esc(T('picker_title')) + '" hidden>' +
          '<div class="sfcm-sheet__bg" data-close></div>' +
          '<div class="sfcm-sheet__panel">' +
            '<div class="sfcm-sheet__top"><p class="sfcm-sheet__t">' + esc(T('picker_title')) + '</p><button type="button" class="sfcm-x" data-close aria-label="' + esc(T('close')) + '">' + ICON.x + '</button></div>' +
            '<label class="sfcm-search">' + ICON.search + '<input type="search" data-q placeholder="' + esc(T('search_ph')) + '" autocomplete="off" aria-label="' + esc(T('search_ph')) + '"></label>' +
            '<div class="sfcm-brands" data-brands></div>' +
            '<ul class="sfcm-list" data-list role="listbox"></ul>' +
          '</div>' +
        '</div>';
    }

    function renderQuick() {
      var box = $('[data-quick]', app), m = matchups();
      if (!m.length) { box.hidden = true; return; }
      box.innerHTML = '<p class="sfcm-quick__l">' + esc(T('matchups')) + '</p><div class="sfcm-quick__grid">' + m.map(function (pair) {
        var on = pair[0] === sel[0] && pair[1] === sel[1];
        return '<button type="button" class="sfcm-match' + (on ? ' is-on' : '') + '" data-pair="' + esc(pair.join(',')) + '">' +
          '<span style="--c:' + SLOT_COLORS[0] + '">' + esc(short(byH[pair[0]].title)) + '</span><em>' + esc(T('vs')) + '</em>' +
          '<span style="--c:' + SLOT_COLORS[1] + '">' + esc(short(byH[pair[1]].title)) + '</span></button>';
      }).join('') + '</div>';
    }
    // "Bambu Lab P2S Combo" reads as "Bambu Lab P2S" where space is short
    function short(t) { return bareTitle(t).replace(/ Combo$/i, ''); }

    function renderSlots() {
      var n = cap(), html = '';
      for (var i = 0; i < n; i++) {
        var p = byH[sel[i]];
        if (p) {
          html += '<article class="sfcm-slot" style="--c:' + SLOT_COLORS[i] + '">' +
            '<span class="sfcm-tag">' + esc(T('printer_x', { x: SLOT_NAMES[i] })) + '</span>' +
            '<button type="button" class="sfcm-slot__rm" data-rm="' + i + '" aria-label="' + esc(T('remove')) + ': ' + esc(p.title) + '">' + ICON.x + '</button>' +
            '<a class="sfcm-slot__img" href="' + esc(p.url) + '">' + (p.img ? '<img src="' + esc(p.img) + '" alt="" loading="lazy" width="240" height="240">' : '<span class="sfcm-slot__noimg">' + ICON.enclosed + '<b>' + esc(p.brand) + '</b></span>') + '</a>' +
            '<p class="sfcm-slot__brand">' + esc(p.brand) + '</p>' +
            '<h3 class="sfcm-slot__t"><a href="' + esc(p.url) + '">' + esc(p.title) + '</a></h3>' +
            '<p class="sfcm-pp">' + (p.cs ? '<s>' + esc(p.cs) + '</s> ' : '') + '<b>' + esc(p.ps) + '</b></p>' +
            '<div class="sfcm-slot__act">' + buyHTML(p) +
            '<button type="button" class="sfcm-btn sfcm-btn--ghost" data-pick="' + i + '">' + ICON.swap + '<span>' + esc(T('change')) + '</span></button></div>' +
            '</article>';
        } else {
          html += '<button type="button" class="sfcm-slot sfcm-slot--empty" data-pick="' + i + '" style="--c:' + SLOT_COLORS[i] + '">' +
            '<span class="sfcm-slot__plus">' + ICON.plus + '</span><span>' + esc(T('select_x', { x: SLOT_NAMES[i] })) + '</span></button>';
        }
      }
      $('[data-slots]', app).innerHTML = html;
      $('[data-slots]', app).style.setProperty('--n', n);
    }
    function buyHTML(p) {
      if (!p.available) return '<a class="sfcm-btn" href="' + esc(p.url) + '">' + esc(T('view')) + '</a>';
      if (p.nv > 1) return '<a class="sfcm-btn" href="' + esc(p.url) + '">' + esc(T('options')) + '</a>';
      return '<form method="post" action="/cart/add" class="sfcm-buy"><input type="hidden" name="id" value="' + esc(p.vid) + '"><input type="hidden" name="quantity" value="1">' +
        '<button type="submit" class="sfcm-btn">' + esc(T('add_cart')) + '</button></form>';
    }

    function cellsFor(key, ps) {
      var r = ROWS[key];
      var vals = ps.map(function (p) { return p ? r.val(p.s, p) : null; });
      var have = vals.filter(function (v) { return v != null; });
      var best = null;
      if (r.better && have.length > 1) {
        var b = r.better === 'high' ? Math.max.apply(null, have) : Math.min.apply(null, have);
        if (!have.every(function (v) { return v === b; })) best = b;
      }
      var texts = [];
      var cells = ps.map(function (p, i) {
        if (!p) return '<div class="sfcm-cell is-empty"><span></span></div>';
        var v = vals[i], body;
        if (r.html) body = r.html(p.s, p);
        else if (v == null) body = null;
        else {
          var main = r.txt ? r.txt(p.s) : fmtN(v) + (r.unit ? ' ' + r.unit : '');
          var sub = r.sub ? r.sub(p.s) : '';
          body = '<span class="sfcm-v">' + esc(main) + '</span>' + (sub ? '<small>' + esc(sub) + '</small>' : '');
          if (r.bar) body += '<span class="sfcm-bar"><i style="--w:' + Math.max(4, v / r.max * 100).toFixed(1) + '%"></i></span>';
          if (r.thermo) body += thermo(r, v);
        }
        texts.push(body == null ? '' : body.replace(/<[^>]+>/g, ''));
        if (body == null) return '<div class="sfcm-cell is-na"><span>' + esc(T('not_listed')) + '</span></div>';
        var isBest = best != null && v === best;
        return '<div class="sfcm-cell' + (isBest ? ' is-best' : '') + '" style="--c:' + SLOT_COLORS[i] + '">' +
          (isBest ? '<span class="sfcm-best">' + esc(T('best')) + '</span>' : '') + body + '</div>';
      });
      var same = texts.length > 1 && texts.every(function (t) { return t === texts[0]; });
      return { html: cells.join(''), same: same };
    }
    function thermo(r, v) {
      var pct = function (x) { return Math.max(0, Math.min(100, (x - r.lo) / (r.hi - r.lo) * 100)); };
      return '<span class="sfcm-thermo"><i style="--w:' + pct(v).toFixed(1) + '%"></i>' + r.thermo.map(function (m) {
        return '<em class="' + (v >= m[0] ? 'is-ok' : '') + '" style="--x:' + pct(m[0]).toFixed(1) + '%"><span>' + esc(m[1]) + '</span></em>';
      }).join('') + '</span>';
    }

    // Build volumes, drawn to one scale and overlaid from the same back corner.
    // The frame is computed from the corner points, so any size fits.
    function isoBoxes(ps) {
      var list = ps.map(function (p, i) { return p && p.s.bv ? { bv: p.s.bv, c: SLOT_COLORS[i], l: p.s.litres } : null; }).filter(Boolean);
      if (!list.length) return '';
      var c30 = Math.cos(Math.PI / 6), xs = [], ys = [];
      function P(x, y, z) { var px = (x - y) * c30, py = (x + y) * 0.5 - z; xs.push(px); ys.push(py); return [px.toFixed(1), py.toFixed(1)]; }
      function poly(pts) { return pts.map(function (q) { return q.join(','); }).join(' '); }
      list.sort(function (a, b) { return b.l - a.l; });
      var g = list.map(function (b, i) {
        var x = b.bv[0], y = b.bv[1], z = b.bv[2];
        var top = [P(0, 0, z), P(x, 0, z), P(x, y, z), P(0, y, z)];
        var left = [P(0, y, 0), P(x, y, 0), P(x, y, z), P(0, y, z)];
        var right = [P(x, 0, 0), P(x, y, 0), P(x, y, z), P(x, 0, z)];
        return '<g class="sfcm-box" style="--c:' + b.c + ';--d:' + (i * 120) + 'ms">' +
          '<polygon class="f1" points="' + poly(left) + '"/><polygon class="f2" points="' + poly(right) + '"/><polygon class="f3" points="' + poly(top) + '"/>' +
          '<polyline class="e" points="' + poly([P(0, y, z), P(0, 0, z), P(x, 0, z)]) + '"/>' +
          '<polyline class="e" points="' + poly([P(0, 0, z), P(0, 0, 0)]) + '" stroke-dasharray="6 6"/>' +
          '</g>';
      }).join('');
      // A 330 ml can (66 mm across, 122 mm tall) at the same scale, standing in
      // front of the left corner, for a sense of size.
      var maxY = Math.max.apply(null, list.map(function (b) { return b.bv[1]; }));
      var base = P(0, maxY, 0), cw = 66, ch = 122, cx = +base[0] - cw - 24, cy = +base[1] + 10;
      xs.push(cx); ys.push(cy - ch, cy);
      var can = '<g class="sfcm-can"><rect x="' + cx.toFixed(1) + '" y="' + (cy - ch).toFixed(1) + '" width="' + cw + '" height="' + ch + '" rx="12"/>' +
        '<ellipse cx="' + (cx + cw / 2).toFixed(1) + '" cy="' + (cy - ch + 6).toFixed(1) + '" rx="' + (cw / 2 - 4) + '" ry="5"/></g>';
      var pad = 12, x0 = Math.min.apply(null, xs) - pad, y0 = Math.min.apply(null, ys) - pad;
      var w = Math.max.apply(null, xs) + pad - x0, h = Math.max.apply(null, ys) + pad - y0;
      // which colour is which printer, biggest first like the drawing
      var legend = '<ul class="sfcm-legend">' + ps.map(function (p, i) {
        return p && p.s.bv ? '<li style="--c:' + SLOT_COLORS[i] + '"><i></i><b>' + esc(SLOT_NAMES[i]) + '</b><span>' + esc(short(p.title)) + '</span><small>' + fmtN(p.s.litres) + ' ' + esc(T('vol_unit')) + '</small></li>' : '';
      }).join('') + '<li class="is-can"><i></i><span>' + esc(T('can_l')) + '</span></li></ul>';
      return '<div class="sfcm-iso">' + legend + '<svg viewBox="' + [x0, y0, w, h].map(function (v) { return v.toFixed(1); }).join(' ') + '" role="img" aria-label="' + esc(T('size_l')) + '" style="--sw:' + (w / 300).toFixed(2) + '">' + g + can + '</svg>' +
        '<p class="sfcm-iso__note">' + esc(T('can_note')) + '</p></div>';
    }

    function renderTable() {
      var n = cap(), ps = [];
      for (var i = 0; i < n; i++) ps.push(byH[sel[i]] || null);
      var t = $('[data-table]', app);
      var any = !!(sel[0] || sel[1]);
      $('[data-tools]', app).hidden = !any;
      if (!any) { t.innerHTML = ''; return; }
      var html = '<div class="sfcm-tr sfcm-tr--head"><div class="sfcm-th sfcm-corner"><span></span></div>' + ps.map(function (p, i) {
        return '<div class="sfcm-th" style="--c:' + SLOT_COLORS[i] + '"><span>' + esc(T('printer_x', { x: SLOT_NAMES[i] })) + '</span><b>' +
          (p ? esc(short(p.title)) : '<button type="button" data-pick="' + i + '">' + esc(T('select_x', { x: SLOT_NAMES[i] })) + '</button>') + '</b></div>';
      }).join('') + '</div>';
      GROUPS.forEach(function (gr) {
        html += '<div class="sfcm-tr sfcm-tr--grp"><h3 class="sfcm-grp__t">' + esc(T('g_' + gr.id)) + '</h3></div>';
        gr.rows.forEach(function (key) {
          var r = ROWS[key];
          var info = r.info ? '<button type="button" class="sfcm-i" data-info="' + key + '" aria-label="' + esc(T('what_is', { x: T(key + '_l') })) + '"><span aria-hidden="true">i</span></button>' : '';
          var lab = '<div class="sfcm-lab"><span>' + esc(T(key + '_l')) + '</span>' + info + '</div>';
          // the size drawing is the one cell that spans both printers
          if (r.joint) html += '<div class="sfcm-tr sfcm-tr--joint" data-row="' + key + '">' + lab + '<div class="sfcm-cell sfcm-cell--joint">' + isoBoxes(ps) + '</div></div>';
          else html += '<div class="sfcm-tr" data-row="' + key + '">' + lab + cellsFor(key, ps).html + '</div>';
        });
      });
      t.innerHTML = '<div class="sfcm-tbl">' + html + '</div>';
      // let bars grow from zero each time the selection changes
      if (!reduce) { t.classList.remove('is-in'); void t.offsetWidth; requestAnimationFrame(function () { t.classList.add('is-in'); }); }
      else t.classList.add('is-in');

      $('[data-sticky]', app).style.setProperty('--n', n);
      $('[data-sticky]', app).innerHTML = '<div><span></span></div>' + ps.map(function (p, i) {
        return '<div class="sfcm-sticky__c" style="--c:' + SLOT_COLORS[i] + '"><em>' + esc(SLOT_NAMES[i]) + '</em>' + (p ? '<b>' + esc(short(p.title)) + '</b>' : '') + '</div>';
      }).join('');
    }

    function render() { renderQuick(); renderSlots(); renderTable(); writeURL(); }

    /* -------- picker */
    var picker, pickIndex = 0, brand = '', lastFocus = null;
    function openPicker(i) {
      pickIndex = i; lastFocus = document.activeElement;
      picker.hidden = false;
      document.documentElement.classList.add('sfcm-lock');
      $('[data-q]', picker).value = '';
      renderBrands(); renderList();
      requestAnimationFrame(function () { picker.classList.add('is-open'); if (hover) $('[data-q]', picker).focus(); });
    }
    function closePicker() {
      picker.classList.remove('is-open');
      document.documentElement.classList.remove('sfcm-lock');
      setTimeout(function () { picker.hidden = true; }, reduce ? 0 : 220);
      if (lastFocus) lastFocus.focus({ preventScroll: true });
    }
    function renderBrands() {
      var bs = [];
      printers.forEach(function (p) { if (bs.indexOf(p.brand) < 0) bs.push(p.brand); });
      $('[data-brands]', picker).innerHTML = [''].concat(bs).map(function (b) {
        return '<button type="button" class="sfcm-chip' + (b === brand ? ' is-on' : '') + '" data-brand="' + esc(b) + '">' + esc(b || T('all_brands')) + '</button>';
      }).join('');
    }
    function renderList() {
      var q = $('[data-q]', picker).value.trim().toLowerCase();
      var words = q.split(/\s+/).filter(Boolean);
      var list = printers.filter(function (p) {
        if (brand && p.brand !== brand) return false;
        var hay = (p.title + ' ' + p.brand + ' ' + p.tags.join(' ')).toLowerCase();
        return words.every(function (w) { return hay.indexOf(w) > -1; });
      });
      var ul = $('[data-list]', picker);
      if (!list.length) { ul.innerHTML = '<li class="sfcm-none">' + esc(T('no_results')) + '</li>'; return; }
      ul.innerHTML = list.map(function (p) {
        var used = sel.indexOf(p.h) > -1 && sel[pickIndex] !== p.h;
        var chips = [];
        if (p.s.bv) chips.push(p.s.bv.join('×'));
        if (p.s.speed) chips.push(p.s.speed + ' mm/s');
        if (p.s.enc) chips.push(T('enc_' + p.s.enc));
        if (p.s.colors && (p.s.colors[1] || p.s.colors[0]) > 1) chips.push(T('colors_upto', { m: p.s.colors[1] || p.s.colors[0] }));
        return '<li><button type="button" role="option" class="sfcm-opt' + (used ? ' is-used' : '') + '" data-choose="' + esc(p.h) + '"' + (used ? ' aria-disabled="true"' : '') + '>' +
          (p.img ? '<img src="' + esc(p.img) + '" alt="" loading="lazy" width="64" height="64">' : '<span class="sfcm-opt__ph"></span>') +
          '<span class="sfcm-opt__txt"><b>' + esc(p.title) + '</b><span class="sfcm-opt__chips">' + chips.map(function (c) { return '<em>' + esc(c) + '</em>'; }).join('') + '</span>' +
          (used ? '<small>' + esc(T('already')) + '</small>' : '') + '</span>' +
          '<span class="sfcm-pp">' + (p.cs ? '<s>' + esc(p.cs) + '</s> ' : '') + '<b>' + esc(p.ps) + '</b></span></button></li>';
      }).join('');
    }
    function choose(h) {
      if (sel.indexOf(h) > -1 && sel[pickIndex] !== h) return;
      var next = sel.slice();
      next[pickIndex] = h;
      sel = next.slice(0, 2);
      closePicker();
      render();
      var slots = $('[data-slots]', app);
      if (slots && slots.getBoundingClientRect().top < 0) slots.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    }

    /* -------- spec explainers: hover card on desktop, bottom sheet on touch */
    var pop, popKey = null, popPinned = false, popBtn = null, hideT = null;
    function popHTML(key) {
      return '<button type="button" class="sfcm-x" data-popclose aria-label="' + esc(T('close')) + '">' + ICON.x + '</button>' +
        '<div class="sfcm-pop__ill" aria-hidden="true">' + (ILL[key] || '') + '</div>' +
        '<p class="sfcm-pop__t">' + esc(T('i_' + key + '_t')) + '</p>' +
        '<p class="sfcm-pop__b">' + esc(T('i_' + key + '_b')) + '</p>' +
        '<p class="sfcm-pop__w"><b>' + esc(T('why')) + '</b> ' + esc(T('i_' + key + '_w')) + '</p>' +
        (key === 'materials' ? '<p class="sfcm-pop__n">' + esc(T('estimated')) + '</p>' : '');
    }
    function showPop(btn, pinned) {
      clearTimeout(hideT);
      var key = btn.getAttribute('data-info');
      if (popKey !== key) pop.innerHTML = popHTML(key);
      popKey = key; popPinned = !!pinned;
      pop.hidden = false;
      pop.classList.toggle('is-sheet', !hover);
      $$('.sfcm-i[aria-expanded="true"]', app).forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
      btn.setAttribute('aria-expanded', 'true');
      popBtn = btn;
      if (hover) {
        place();
      } else {
        document.documentElement.classList.add('sfcm-lock');
      }
      requestAnimationFrame(function () { pop.classList.add('is-open'); });
    }
    // On wide screens the card sits beside its i button, over the table
    // cells, so it never covers the label or the site header; with no room
    // beside it, it hangs below (or above). It follows the button on scroll.
    function place() {
      if (!popBtn || pop.hidden || !hover) return;
      var r = popBtn.getBoundingClientRect(), w = pop.offsetWidth, h = pop.offsetHeight;
      if (r.bottom < 0 || r.top > innerHeight) { hidePop(); return; }
      var top = (parseFloat(getComputedStyle(root).getPropertyValue('--sfcm-hdr')) || 0) + 12;
      var rtl = (document.documentElement.dir || '').toLowerCase() === 'rtl';
      var x, y, side = rtl ? r.left - w - 14 : r.right + 14;
      if (side >= 12 && side + w <= innerWidth - 12) {
        x = side;
        y = Math.min(Math.max(top, r.top + r.height / 2 - 60), innerHeight - h - 12);
      } else {
        x = Math.min(Math.max(12, r.left + r.width / 2 - w / 2), innerWidth - w - 12);
        y = r.bottom + 10;
        if (y + h > innerHeight - 12) y = Math.max(top, r.top - h - 10);
      }
      pop.style.left = x + 'px'; pop.style.top = Math.max(top, y) + 'px';
    }
    function hidePop() {
      if (pop.hidden) return;
      pop.classList.remove('is-open');
      document.documentElement.classList.remove('sfcm-lock');
      $$('.sfcm-i[aria-expanded="true"]', app).forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
      popKey = null; popPinned = false;
      setTimeout(function () { if (!popKey) pop.hidden = true; }, reduce ? 0 : 180);
    }

    /* -------- events */
    function wire() {
      picker = $('[data-picker]', app);
      pop = $('[data-pop]', app);
      // the overlays live on <body> so no ancestor transform or overflow can clip them
      document.body.appendChild(picker);
      document.body.appendChild(pop);
      picker.classList.add('sfcm-portal'); pop.classList.add('sfcm-portal');
      picker.setAttribute('dir', document.documentElement.dir || 'ltr');
      pop.setAttribute('dir', document.documentElement.dir || 'ltr');

      app.addEventListener('click', function (e) {
        var t = e.target.closest('button, a');
        if (!t) return;
        if (t.hasAttribute('data-pick')) openPicker(+t.getAttribute('data-pick'));
        else if (t.hasAttribute('data-rm')) { sel[+t.getAttribute('data-rm')] = null; render(); }
        else if (t.hasAttribute('data-pair')) { sel = t.getAttribute('data-pair').split(','); render(); }
        else if (t.hasAttribute('data-info')) {
          if (popKey === t.getAttribute('data-info') && popPinned) hidePop();
          else showPop(t, true);
        } else if (t.hasAttribute('data-share')) share(t);
      });

      picker.addEventListener('click', function (e) {
        var t = e.target.closest('[data-close], [data-brand], [data-choose]');
        if (!t) return;
        if (t.hasAttribute('data-close')) closePicker();
        else if (t.hasAttribute('data-brand')) { brand = t.getAttribute('data-brand'); renderBrands(); renderList(); }
        else if (t.getAttribute('aria-disabled') !== 'true') choose(t.getAttribute('data-choose'));
      });
      $('[data-q]', picker).addEventListener('input', renderList);
      $('[data-q]', picker).addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { var f = $('.sfcm-opt:not(.is-used)', picker); if (f) { e.preventDefault(); choose(f.getAttribute('data-choose')); } }
        if (e.key === 'ArrowDown') { var o = $('.sfcm-opt', picker); if (o) { e.preventDefault(); o.focus(); } }
      });
      $('[data-list]', picker).addEventListener('keydown', function (e) {
        var opts = $$('.sfcm-opt', picker), i = opts.indexOf(document.activeElement);
        if (e.key === 'ArrowDown' && i < opts.length - 1) { e.preventDefault(); opts[i + 1].focus(); }
        if (e.key === 'ArrowUp') { e.preventDefault(); (i > 0 ? opts[i - 1] : $('[data-q]', picker)).focus(); }
      });

      if (hover) {
        app.addEventListener('mouseover', function (e) {
          var b = e.target.closest('[data-info]');
          if (b && !popPinned) showPop(b, false);
        });
        app.addEventListener('mouseout', function (e) {
          var b = e.target.closest('[data-info]');
          if (b && !popPinned && !(e.relatedTarget && pop.contains(e.relatedTarget))) hideT = setTimeout(hidePop, 160);
        });
        pop.addEventListener('mouseenter', function () { clearTimeout(hideT); });
        pop.addEventListener('mouseleave', function () { if (!popPinned) hideT = setTimeout(hidePop, 160); });
        app.addEventListener('focusin', function (e) { var b = e.target.closest('[data-info]'); if (b) showPop(b, false); });
      }
      pop.addEventListener('click', function (e) { if (e.target.closest('[data-popclose]') || e.target === pop) hidePop(); });
      document.addEventListener('click', function (e) {
        if (!pop.hidden && popPinned && !pop.contains(e.target) && !e.target.closest('[data-info]')) hidePop();
      });
      document.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape') return;
        if (!picker.hidden) closePicker(); else hidePop();
      });
      addEventListener('scroll', function () { if (pop.hidden || !hover) return; if (popPinned) place(); else hidePop(); }, { passive: true });
      addEventListener('resize', place, { passive: true });


      // the compact names bar appears once the big cards have scrolled away,
      // and only while the table is on screen
      var sticky = $('[data-sticky]', app), slots = $('[data-slots]', app), table = $('[data-table]', app);
      if ('IntersectionObserver' in window) {
        var st = { slotsGone: false, tableIn: false };
        var paint = function () { sticky.classList.toggle('is-on', st.slotsGone && st.tableIn); };
        new IntersectionObserver(function (es) { st.slotsGone = !es[0].isIntersecting && es[0].boundingClientRect.top < 0; paint(); }).observe(slots);
        new IntersectionObserver(function (es) { st.tableIn = es[0].isIntersecting; paint(); }, { rootMargin: '-120px 0px -40% 0px' }).observe(table);
      }
      var header = document.querySelector('.shopify-section-header-sticky, .section-header');
      var setHdr = function () {
        var hb = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
        root.style.setProperty('--sfcm-hdr', hb + 'px');
      };
      addEventListener('scroll', setHdr, { passive: true }); setHdr();
    }

    function share(btn) {
      var url = location.href, label = $('span', btn), old = label.textContent;
      var done = function () { label.textContent = T('copied'); btn.classList.add('is-done'); setTimeout(function () { label.textContent = old; btn.classList.remove('is-done'); }, 1800); };
      if (navigator.share && !hover) { navigator.share({ title: document.title, url: url }).catch(function () {}); return; }
      if (navigator.clipboard) navigator.clipboard.writeText(url).then(done, function () {});
    }

    shell();
    wire();
    sel = readURL();
    render();
  }

  function boot(scope) { $$('[data-sfcm]', scope).forEach(Compare); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(); });
  else boot();
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
